// Firestore service for Sales Tax Rules management
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'salesTaxRules';
const ARCHIVE_COLLECTION_NAME = 'salesTaxArchive';

export const salesTaxRulesService = {
  // Determine jurisdiction level from jurisdiction object
  getJurisdictionLevel: (jurisdiction) => {
    if (!jurisdiction) return 'federal';
    if (jurisdiction.zipCode) return 'zipcode';
    if (jurisdiction.city) return 'city';
    if (jurisdiction.county) return 'county';
    if (jurisdiction.state) return 'state';
    return 'federal';
  },

  // Create jurisdiction ID for unique identification
  createJurisdictionId: (jurisdiction, level) => {
    if (level === 'federal') return 'FEDERAL';
    if (level === 'state') return `STATE_${jurisdiction.state}`;
    if (level === 'county') return `COUNTY_${jurisdiction.state}_${jurisdiction.county}`;
    if (level === 'city') return `CITY_${jurisdiction.state}_${jurisdiction.county}_${jurisdiction.city}`;
    if (level === 'zipcode') return `ZIP_${jurisdiction.state}_${jurisdiction.county}_${jurisdiction.city}_${jurisdiction.zipCode}`;
    return null;
  },

  // Get all sales tax rules with real-time updates
  subscribeToRules: (callback, filters = {}) => {
    let q = collection(db, COLLECTION_NAME);
    
    // Apply filters
    if (filters.jurisdiction) {
      q = query(q, where('jurisdiction.state', '==', filters.jurisdiction));
    }
    
    if (filters.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    // Default ordering
    q = query(q, orderBy('precedence', 'desc'), orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const rules = [];
      snapshot.forEach((doc) => {
        rules.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(rules);
    });
  },

  // Get hierarchical jurisdiction data for new interface
  getHierarchicalJurisdictions: async (filters = {}) => {
    // Get all tax rules
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const allRules = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      allRules.push({
        id: doc.id,
        ...data,
        level: salesTaxRulesService.getJurisdictionLevel(data.jurisdiction)
      });
    });
    
    // Build hierarchy with federal level
    return salesTaxRulesService.buildJurisdictionHierarchy(allRules, filters);
  },

  // Get federal tax rate
  getFederalTaxRate: async () => {
    const docRef = doc(db, COLLECTION_NAME, 'FEDERAL');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data().taxRate || 0;
    }
    return null;
  },

  // Get tax rate for specific jurisdiction
  getJurisdictionTaxRate: async (jurisdiction, level) => {
    const jurisdictionId = salesTaxRulesService.createJurisdictionId(jurisdiction, level);
    if (!jurisdictionId) return null;
    
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data().taxRate || 0;
    }
    return null;
  },

  // Update tax rate for specific jurisdiction
  updateJurisdictionTaxRate: async (jurisdiction, level, taxRate) => {
    const jurisdictionId = salesTaxRulesService.createJurisdictionId(jurisdiction, level);
    if (!jurisdictionId) throw new Error('Invalid jurisdiction');
    
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    
    const ruleData = {
      jurisdiction: level === 'federal' ? {} : jurisdiction,
      level: level,
      taxRate: taxRate,
      isActive: true,
      updatedAt: serverTimestamp(),
      lastModified: new Date().toISOString()
    };
    
    await setDoc(docRef, ruleData, { merge: true });
    return { id: jurisdictionId, ...ruleData };
  },

  // Build hierarchical structure from flat rules data
  buildJurisdictionHierarchy: (rules, filters = {}) => {
    const hierarchy = new Map(); // state -> counties -> cities -> zipCodes
    const ratesByLevel = {
      federal: null,
      state: new Map(),
      county: new Map(),
      city: new Map(),
      zipcode: new Map()
    };
    
    // First, organize rules by level and jurisdiction
    rules.forEach(rule => {
      const level = rule.level;
      const jurisdiction = rule.jurisdiction || {};
      
      // Get tax rate from new format or fallback to old format
      let taxRate = rule.taxRate;
      if (taxRate === undefined || taxRate === null) {
        // Fallback to old data structure only for legacy data
        if (rule.rateConfig) {
          taxRate = salesTaxRulesService.calculateCombinedRateFromOldFormat(rule.rateConfig);
        } else {
          taxRate = 0;
        }
      }
      
      // Debug log to track rate values
      if (rule.level) {
        console.debug(`📊 ${rule.level} rate for ${rule.id}:`, taxRate);
      }
      
      if (level === 'federal') {
        ratesByLevel.federal = taxRate;
      } else if (level === 'state' && jurisdiction.state) {
        ratesByLevel.state.set(jurisdiction.state, taxRate);
      } else if (level === 'county' && jurisdiction.state && jurisdiction.county) {
        const key = `${jurisdiction.state}_${jurisdiction.county}`;
        ratesByLevel.county.set(key, taxRate);
      } else if (level === 'city' && jurisdiction.state && jurisdiction.county && jurisdiction.city) {
        const key = `${jurisdiction.state}_${jurisdiction.county}_${jurisdiction.city}`;
        ratesByLevel.city.set(key, taxRate);
      } else if (level === 'zipcode' && jurisdiction.zipCode) {
        const key = `${jurisdiction.state}_${jurisdiction.county}_${jurisdiction.city}_${jurisdiction.zipCode}`;
        ratesByLevel.zipcode.set(key, taxRate);
      }
    });
    
    // Get unique jurisdictions from all levels
    const jurisdictions = new Set();
    rules.forEach(rule => {
      const jurisdiction = rule.jurisdiction || {};
      
      // For existing old format data, extract jurisdiction info
      if (rule.level === 'zipcode' && jurisdiction.zipCode) {
        jurisdictions.add(JSON.stringify({
          state: jurisdiction.state,
          county: jurisdiction.county,
          city: jurisdiction.city,
          zipCode: jurisdiction.zipCode
        }));
      }
      // Also add any other jurisdiction info from non-zipcode rules
      else if (jurisdiction.state) {
        // For old data that might not have level field, try to infer structure
        if (jurisdiction.zipCode) {
          jurisdictions.add(JSON.stringify({
            state: jurisdiction.state,
            county: jurisdiction.county || 'Unknown County',
            city: jurisdiction.city || 'Unknown City',
            zipCode: jurisdiction.zipCode
          }));
        }
      }
    });
    
    // Build hierarchy from unique jurisdictions
    jurisdictions.forEach(jurisdictionStr => {
      const jurisdiction = JSON.parse(jurisdictionStr);
      const { state, county, city, zipCode } = jurisdiction;
      
      if (!state) return;
      
      // Initialize state if not exists
      if (!hierarchy.has(state)) {
        const stateRate = ratesByLevel.state.get(state);
        hierarchy.set(state, {
          id: `STATE_${state}`,
          name: state,
          type: 'state',
          state: state,
          taxRate: stateRate !== undefined ? stateRate : null,
          counties: new Map()
        });
      }
      
      const stateData = hierarchy.get(state);
      
      // Initialize county if not exists
      if (county && !stateData.counties.has(county)) {
        const countyKey = `${state}_${county}`;
        const countyRate = ratesByLevel.county.get(countyKey);
        stateData.counties.set(county, {
          id: `COUNTY_${countyKey}`,
          name: county,
          type: 'county',
          state: state,
          county: county,
          taxRate: countyRate !== undefined ? countyRate : null,
          cities: new Map()
        });
      }
      
      if (county) {
        const countyData = stateData.counties.get(county);
        
        // Initialize city if not exists
        if (city && !countyData.cities.has(city)) {
          const cityKey = `${state}_${county}_${city}`;
          const cityRate = ratesByLevel.city.get(cityKey);
          countyData.cities.set(city, {
            id: `CITY_${cityKey}`,
            name: city,
            type: 'city',
            state: state,
            county: county,
            city: city,
            taxRate: cityRate !== undefined ? cityRate : null,
            zipCodes: new Map()
          });
        }
        
        if (city) {
          const cityData = countyData.cities.get(city);
          
          // Add ZIP code data
          if (zipCode) {
            const zipKey = `${state}_${county}_${city}_${zipCode}`;
            const zipRate = ratesByLevel.zipcode.get(zipKey);
            cityData.zipCodes.set(zipCode, {
              id: `ZIP_${zipKey}`,
              zipCode: zipCode,
              type: 'zipcode',
              state: state,
              county: county,
              city: city,
              taxRate: zipRate !== undefined ? zipRate : null,
              isActive: true
            });
          }
        }
      }
    });
    
    // Apply filters
    let result = salesTaxRulesService.convertHierarchyToArray(hierarchy, ratesByLevel.federal);
    
    if (filters.state) {
      result.jurisdictions = result.jurisdictions.filter(item => item.state === filters.state);
    }
    if (filters.county) {
      result.jurisdictions = result.jurisdictions.filter(item => item.children.some(county => county.county === filters.county));
    }
    if (filters.city) {
      result.jurisdictions = result.jurisdictions.filter(item => 
        item.children.some(county => 
          county.children.some(city => city.city === filters.city)
        )
      );
    }
    
    return result;
  },

  // Convert hierarchy map to array format for table
  convertHierarchyToArray: (hierarchy, federalRate) => {
    const result = [];
    
    hierarchy.forEach((stateData) => {
      const stateChildren = [];
      
      stateData.counties.forEach((countyData) => {
        const countyChildren = [];
        
        countyData.cities.forEach((cityData) => {
          const cityChildren = [];
          
          cityData.zipCodes.forEach((zipData) => {
            cityChildren.push(zipData);
          });
          
          countyChildren.push({
            ...cityData,
            children: cityChildren,
            totalZipCodes: cityChildren.length
          });
        });
        
        stateChildren.push({
          ...countyData,
          children: countyChildren,
          totalZipCodes: countyChildren.reduce((sum, city) => sum + city.totalZipCodes, 0)
        });
      });
      
      result.push({
        ...stateData,
        children: stateChildren,
        totalZipCodes: stateChildren.reduce((sum, county) => sum + county.totalZipCodes, 0)
      });
    });
    
    return { federalRate, jurisdictions: result };
  },


  // Get available states for filter dropdown
  getAvailableStates: async () => {
    const q = query(collection(db, COLLECTION_NAME));
    const snapshot = await getDocs(q);
    const states = new Set();
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.jurisdiction && data.jurisdiction.state) {
        states.add(data.jurisdiction.state);
      }
    });
    
    return Array.from(states).sort();
  },

  // Get available counties for a state
  getAvailableCounties: async (state) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('jurisdiction.state', '==', state)
    );
    const snapshot = await getDocs(q);
    const counties = new Set();
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.jurisdiction && data.jurisdiction.county) {
        counties.add(data.jurisdiction.county);
      }
    });
    
    return Array.from(counties).sort();
  },

  // Get available cities for a state/county
  getAvailableCities: async (state, county = null) => {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('jurisdiction.state', '==', state)
    );
    
    if (county) {
      q = query(q, where('jurisdiction.county', '==', county));
    }
    
    const snapshot = await getDocs(q);
    const cities = new Set();
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.jurisdiction && data.jurisdiction.city) {
        cities.add(data.jurisdiction.city);
      }
    });
    
    return Array.from(cities).sort();
  },

  // Bulk update tax rates for specific level
  bulkUpdateLevelRates: async (jurisdictions, level, taxRate) => {
    const batch = writeBatch(db);
    const results = { updated: [], errors: [] };
    
    for (const jurisdiction of jurisdictions) {
      try {
        const jurisdictionId = salesTaxRulesService.createJurisdictionId(jurisdiction, level);
        if (!jurisdictionId) {
          results.errors.push({ jurisdiction, error: 'Invalid jurisdiction' });
          continue;
        }
        
        const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
        
        const ruleData = {
          jurisdiction: level === 'federal' ? {} : jurisdiction,
          level: level,
          taxRate: taxRate,
          isActive: true,
          updatedAt: serverTimestamp(),
          lastModified: new Date().toISOString(),
          modifiedBy: 'bulk_edit'
        };
        
        batch.set(docRef, ruleData, { merge: true });
        results.updated.push(jurisdictionId);
        
      } catch (error) {
        results.errors.push({ jurisdiction, error: error.message });
      }
    }
    
    await batch.commit();
    return results;
  },

  // Apply rate to all ZIP codes in same city
  applyRateToSameCityZipCodes: async (jurisdiction, taxRate) => {
    if (!jurisdiction.state || !jurisdiction.county || !jurisdiction.city) {
      throw new Error('City jurisdiction must have state, county, and city');
    }
    
    // Get all ZIP codes in the same city
    const q = query(
      collection(db, COLLECTION_NAME),
      where('jurisdiction.state', '==', jurisdiction.state),
      where('jurisdiction.county', '==', jurisdiction.county),
      where('jurisdiction.city', '==', jurisdiction.city),
      where('level', '==', 'zipcode')
    );
    
    const snapshot = await getDocs(q);
    const zipCodeJurisdictions = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.jurisdiction && data.jurisdiction.zipCode) {
        zipCodeJurisdictions.push(data.jurisdiction);
      }
    });
    
    return salesTaxRulesService.bulkUpdateLevelRates(zipCodeJurisdictions, 'zipcode', taxRate);
  },

  // Helper method for backward compatibility with old data structure
  calculateCombinedRateFromOldFormat: (rateConfig) => {
    if (!rateConfig) return 0;
    
    let totalRate = 0;
    
    // Handle bulk import format with detailed breakdown
    if (rateConfig.detailedBreakdown) {
      const { stateRate, countyRate, cityRate, districtRate } = rateConfig.detailedBreakdown;
      totalRate = (stateRate || 0) + (countyRate || 0) + (cityRate || 0) + (districtRate || 0);
    } else {
      // Handle standard format
      totalRate = rateConfig.totalRate || rateConfig.baseRate || 0;
    }
    
    // IMPORTANT: All rates should now be in decimal format (0.09375 for 9.375%)
    // We no longer convert since CSV data is already in correct decimal format
    // Only convert if we detect legacy percentage data (rate > 1)
    if (totalRate > 1) {
      console.warn('🔧 Converting legacy percentage rate to decimal:', totalRate, '→', totalRate / 100);
      return totalRate / 100;
    }
    
    // Return as-is since it should already be in decimal format
    return totalRate;
  },

  // Search rules across all jurisdiction fields
  searchRules: async (searchTerm, filters = {}) => {
    // Get all rules first, then filter in memory for cross-field search
    let q = collection(db, COLLECTION_NAME);
    
    if (filters.state) {
      q = query(q, where('jurisdiction.state', '==', filters.state));
    }
    
    if (filters.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    const snapshot = await getDocs(q);
    const rules = [];
    const searchLower = searchTerm.toLowerCase();
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const jurisdiction = data.jurisdiction || {};
      
      // Search across multiple fields
      const searchableText = [
        jurisdiction.state,
        jurisdiction.county,
        jurisdiction.city,
        jurisdiction.zipCode,
        data.name,
        data.description
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (searchableText.includes(searchLower)) {
        rules.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return rules;
  },

  // Get single sales tax rule
  getRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    return null;
  },

  // Create new sales tax rule
  createRule: async (ruleData) => {
    const ruleId = ruleData.id || `STR_${Date.now()}`;
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    // Handle bulk import format vs manual format
    const isBulkImport = ruleData.dataSource === 'csv_import' || ruleData.createdBy === 'bulk_import';
    
    // Normalize jurisdiction data (handle both array and string formats)
    let normalizedJurisdiction;
    if (isBulkImport && ruleData.jurisdiction) {
      normalizedJurisdiction = {
        country: ruleData.jurisdiction.country || 'US',
        state: ruleData.jurisdiction.state || '',
        county: (ruleData.jurisdiction.counties && Array.isArray(ruleData.jurisdiction.counties)) 
          ? ruleData.jurisdiction.counties[0] || '' 
          : ruleData.jurisdiction.county || '',
        city: (ruleData.jurisdiction.cities && Array.isArray(ruleData.jurisdiction.cities)) 
          ? ruleData.jurisdiction.cities[0] || '' 
          : ruleData.jurisdiction.city || '',
        zipCode: (ruleData.jurisdiction.zipCodes && Array.isArray(ruleData.jurisdiction.zipCodes)) 
          ? ruleData.jurisdiction.zipCodes[0] || '' 
          : ruleData.jurisdiction.zipCode || '',
        district: ruleData.jurisdiction.district || '',
        // Keep original bulk format for reference
        originalJurisdiction: ruleData.jurisdiction
      };
    } else {
      normalizedJurisdiction = ruleData.jurisdiction || {
        country: 'US',
        state: '',
        county: '',
        city: '',
        zipCode: '',
        district: ''
      };
    }
    
    // For the new jurisdiction-level approach, we don't need complex rateConfig
    // The taxRate field should be used directly for bulk imports
    let normalizedRateConfig = null;
    
    // Only create rateConfig for backward compatibility with manual rules (non-bulk imports)
    if (!isBulkImport && ruleData.rateConfig) {
      normalizedRateConfig = ruleData.rateConfig;
    }
    
    const newRule = {
      ...ruleData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: ruleData.isActive !== undefined ? ruleData.isActive : true,
      // Use normalized data
      jurisdiction: normalizedJurisdiction,
      // Only include rateConfig for non-bulk imports (backward compatibility)
      ...(normalizedRateConfig && { rateConfig: normalizedRateConfig }),
      // Required fields with defaults
      applicableCategories: ruleData.applicableCategories || [],
      exemptions: ruleData.exemptions || [],
      effectiveDate: ruleData.effectiveDate || new Date(),
      expirationDate: ruleData.expirationDate || null,
      precedence: ruleData.precedence || 1000,
      source: ruleData.source || (isBulkImport ? 'bulk_import' : 'manual'),
      // Additional bulk import metadata
      ...(isBulkImport && {
        bulkImportMetadata: {
          originalFormat: 'csv_import',
          importedAt: new Date().toISOString(),
          dataSource: ruleData.dataSource || 'csv_import',
          lastVerified: ruleData.lastVerified || new Date().toISOString()
        }
      })
    };
    
    await setDoc(docRef, newRule);
    return { id: ruleId, ...newRule };
  },

  // Archive existing rule to salesTaxArchive collection
  archiveRuleVersion: async (existingRule, updatedBy = 'system') => {
    const archiveId = `${existingRule.id}_${Date.now()}`;
    const archiveDocRef = doc(db, ARCHIVE_COLLECTION_NAME, archiveId);
    
    const archiveData = {
      ...existingRule,
      originalId: existingRule.id,
      archivedAt: serverTimestamp(),
      archivedBy: updatedBy,
      archiveReason: 'updated_via_bulk_import'
    };
    
    await setDoc(archiveDocRef, archiveData);
    return { id: archiveId, ...archiveData };
  },

  // Update existing rule with archiving
  updateRuleWithArchive: async (ruleId, updateData, updatedBy = 'bulk_import') => {
    // First, get the existing rule
    const existingRule = await salesTaxRulesService.getRule(ruleId);
    if (!existingRule) {
      throw new Error(`Rule ${ruleId} not found`);
    }
    
    // Archive the existing version
    await salesTaxRulesService.archiveRuleVersion(existingRule, updatedBy);
    
    // Update the rule
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    const updates = {
      ...updateData,
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy,
      lastModified: new Date().toISOString()
    };
    
    await updateDoc(docRef, updates);
    return { id: ruleId, ...updates };
  },

  // Update existing rule (without archiving - for backward compatibility)
  updateRule: async (ruleId, updateData) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    const updates = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(docRef, updates);
    return updates;
  },

  // Archive rule (soft delete)
  archiveRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    await updateDoc(docRef, {
      isActive: false,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  },

  // Restore archived rule
  restoreRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    await updateDoc(docRef, {
      isActive: true,
      archivedAt: null,
      updatedAt: serverTimestamp()
    });
  },

  // Delete rule permanently
  deleteRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    await deleteDoc(docRef);
  },

  // Get rules by jurisdiction
  getRulesByJurisdiction: async (jurisdiction) => {
    let q = collection(db, COLLECTION_NAME);
    
    // Build query based on jurisdiction specificity
    if (jurisdiction.zipCode) {
      q = query(q, where('jurisdiction.zipCode', '==', jurisdiction.zipCode));
    } else if (jurisdiction.city) {
      q = query(q, where('jurisdiction.city', '==', jurisdiction.city));
    } else if (jurisdiction.county) {
      q = query(q, where('jurisdiction.county', '==', jurisdiction.county));
    } else if (jurisdiction.state) {
      q = query(q, where('jurisdiction.state', '==', jurisdiction.state));
    }
    
    q = query(q, orderBy('precedence', 'desc'));
    
    const snapshot = await getDocs(q);
    const rules = [];
    snapshot.forEach((doc) => {
      rules.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return rules;
  },


  // Update rule precedence for conflict resolution
  updatePrecedence: async (updates) => {
    const batch = writeBatch(db);
    
    updates.forEach(({ ruleId, precedence }) => {
      const docRef = doc(db, COLLECTION_NAME, ruleId);
      batch.update(docRef, { 
        precedence, 
        updatedAt: serverTimestamp() 
      });
    });
    
    await batch.commit();
  },

  // Detect conflicting rules
  detectConflicts: async (jurisdiction, categoryId) => {
    const rules = await salesTaxRulesService.getRulesByJurisdiction(jurisdiction);
    
    // Find rules that apply to the same category
    const conflictingRules = rules.filter(rule => 
      rule.applicableCategories.includes(categoryId) || 
      rule.applicableCategories.includes('ALL')
    );
    
    return conflictingRules.length > 1 ? conflictingRules : [];
  },

  // Get rules by ZIP code
  getRulesByZipCode: async (zipCode) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('zipCodes', 'array-contains', zipCode)
    );
    
    const snapshot = await getDocs(q);
    const rules = [];
    snapshot.forEach((doc) => {
      rules.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return rules;
  },

  // Bulk create rules
  bulkCreateRules: async (rulesData) => {
    if (!rulesData || rulesData.length === 0) {
      return [];
    }

    const batch = writeBatch(db);
    const results = [];
    
    rulesData.forEach((ruleData, index) => {
      const ruleId = `STR_BULK_${Date.now()}_${index}`;
      const docRef = doc(db, COLLECTION_NAME, ruleId);
      
      const rule = {
        ...ruleData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: ruleData.isActive !== undefined ? ruleData.isActive : true,
        source: 'bulk_import',
        // Set defaults for required fields
        jurisdiction: ruleData.jurisdiction || {
          country: 'US',
          state: '',
          county: '',
          city: '',
          zipCode: '',
          district: ''
        },
        rateConfig: ruleData.rateConfig || {
          baseRate: 0,
          localRate: 0,
          districtRate: 0,
          totalRate: 0
        },
        applicableCategories: ruleData.applicableCategories || [],
        exemptions: ruleData.exemptions || [],
        effectiveDate: ruleData.effectiveDate || new Date(),
        expirationDate: ruleData.expirationDate || null,
        precedence: ruleData.precedence || 1000
      };
      
      batch.set(docRef, rule);
      results.push({ id: ruleId, ...rule });
    });
    
    await batch.commit();
    return results;
  },

  // Validate rule data
  validateRule: (ruleData) => {
    const errors = [];
    
    // Required fields validation
    if (!ruleData.name || ruleData.name.trim() === '') {
      errors.push('Name is required');
    }
    
    if (!ruleData.jurisdictionId || ruleData.jurisdictionId.trim() === '') {
      errors.push('Jurisdiction ID is required');
    }
    
    // Rate configuration validation
    if (ruleData.rateConfig) {
      const { baseRate, localRate, districtRate, totalRate } = ruleData.rateConfig;
      
      // Check rate bounds
      if (totalRate !== undefined && (totalRate < 0 || totalRate > 1)) {
        errors.push('Total rate must be between 0 and 1');
      }
      
      // Check if total rate matches sum of components (with tolerance for floating point)
      if (baseRate !== undefined && localRate !== undefined && 
          districtRate !== undefined && totalRate !== undefined) {
        const calculatedTotal = baseRate + localRate + districtRate;
        const tolerance = 0.0001; // Allow small floating point differences
        
        if (Math.abs(calculatedTotal - totalRate) > tolerance) {
          errors.push('Total rate should equal sum of component rates');
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Bulk import rules (legacy method - now uses bulkCreateRules)
  bulkImportRules: async (rulesData) => {
    return salesTaxRulesService.bulkCreateRules(rulesData);
  }
};

export default salesTaxRulesService;